import React, { useState, useEffect } from "react";
import AiHistoryApiDocs from "./AiHistoryApiDocs";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Typography from "@material-ui/core/Typography";
import Select from "@material-ui/core/Select";
import TextField from "@material-ui/core/TextField";
import Accordion from "@material-ui/core/Accordion";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import CodeIcon from "@material-ui/icons/Code";
import SendIcon from "@material-ui/icons/Send";
import SettingsIcon from "@material-ui/icons/Settings";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import VpnKeyIcon from "@material-ui/icons/VpnKey";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import DescriptionIcon from "@material-ui/icons/Description";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import api from "../../services/api";
import { i18n } from "../../translate/i18n.js";
import toastError from "../../errors/toastError";
import OrganizationSettings from "../../components/OrganizationSettings";

const useStyles = makeStyles(theme => {
	const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

	return ({
	mainPaper: {
		flex: 1,
		paddingTop: 10,
		overflowY: "auto",
		backgroundColor: theme.palette.background.paper,
		borderRadius: "18px 0 0 0",
		border: "none",
		boxShadow: "none",
		...theme.scrollbarStyles,
		[theme.breakpoints.down("sm")]: {
			borderRadius: 0,
		},
	},

	container: {
		maxWidth: 1180,
		padding: theme.spacing(2.5, 3, 4),
		[theme.breakpoints.down("sm")]: {
			padding: theme.spacing(2),
		},
	},

	pageHeader: {
		display: "flex",
		alignItems: "flex-start",
		justifyContent: "space-between",
		gap: theme.spacing(2),
		marginBottom: theme.spacing(2.5),
		flexWrap: "wrap",
	},

	pageIntro: {
		maxWidth: 680,
		color: theme.palette.text.secondary,
		fontSize: "0.95rem",
		lineHeight: 1.55,
		marginTop: theme.spacing(0.5),
	},

	pageTitle: {
		fontWeight: 600,
		fontSize: "1.35rem",
		lineHeight: 1.25,
	},

	accordionSection: {
		marginBottom: theme.spacing(1.5),
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		boxShadow: "none",
		backgroundColor: theme.palette.background.paper,
		overflow: "hidden",
		"&:before": {
			display: "none",
		},
		"&.Mui-expanded": {
			margin: `0 0 ${theme.spacing(1.5)}px`,
		},
	},

	accordionSummary: {
		backgroundColor: isDark ? "rgba(255, 255, 255, 0.03)" : "rgba(0, 0, 0, 0.015)",
		borderBottom: `1px solid ${theme.palette.divider}`,
		minHeight: 60,
		padding: theme.spacing(0, 2.5),
		"&.Mui-expanded": {
			minHeight: 60,
		},
		"& .MuiAccordionSummary-content": {
			alignItems: "center",
			gap: theme.spacing(1.5),
			margin: theme.spacing(1.5, 0),
		},
	},

	sectionIcon: {
		width: 36,
		height: 36,
		borderRadius: 8,
		display: "inline-flex",
		alignItems: "center",
		justifyContent: "center",
		color: theme.palette.primary.main,
		backgroundColor: isDark ? "rgba(117, 45, 230, 0.18)" : "rgba(117, 45, 230, 0.08)",
		flexShrink: 0,
	},

	sectionHeading: {
		display: "flex",
		flexDirection: "column",
		gap: 2,
	},

	sectionTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		lineHeight: 1.25,
	},

	sectionDescription: {
		color: theme.palette.text.secondary,
		fontSize: "0.86rem",
		lineHeight: 1.35,
	},

	accordionDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(2.5),
		padding: theme.spacing(2.5),
	},

	settingRow: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		padding: theme.spacing(1.5, 0),
		borderBottom: `1px solid ${theme.palette.divider}`,
		gap: theme.spacing(2),
		"&:last-child": {
			borderBottom: "none",
		},
		[theme.breakpoints.down("xs")]: {
			alignItems: "stretch",
			flexDirection: "column",
		},
	},

	settingLabel: {
		flex: 1,
		fontWeight: 500,
	},

	settingControl: {
		flex: "0 1 320px",
		[theme.breakpoints.down("xs")]: {
			flex: "1 1 auto",
		},
	},

	tokenField: {
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
	},

	tokenPanel: {
		display: "grid",
		gridTemplateColumns: "minmax(0, 1fr) auto",
		gap: theme.spacing(1.5),
		alignItems: "end",
		[theme.breakpoints.down("sm")]: {
			gridTemplateColumns: "1fr",
		},
	},

	copyButton: {
		marginTop: theme.spacing(1),
		borderRadius: 999,
		textTransform: "none",
		fontWeight: 600,
		boxShadow: "none",
		"&:hover": {
			boxShadow: "none",
		},
	},

	instructionBox: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(1.5),
	},

	instructionTitle: {
		fontWeight: 600,
		color: theme.palette.primary.main,
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		fontSize: "1rem",
	},

	codeBlock: {
		backgroundColor: isDark ? "rgba(0, 0, 0, 0.24)" : "rgba(15, 23, 42, 0.035)",
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		padding: theme.spacing(1.5),
		marginTop: theme.spacing(1),
		marginBottom: theme.spacing(1),
		fontFamily: '"Courier New", monospace',
		fontSize: "0.85rem",
		overflow: "auto",
		whiteSpace: "pre-wrap",
		wordBreak: "break-word",
	},

	instructionText: {
		fontSize: "0.9rem",
		lineHeight: 1.6,
		marginBottom: theme.spacing(1),
	},

	docsSection: {
		display: "grid",
		gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
		gap: theme.spacing(2),
		[theme.breakpoints.down("md")]: {
			gridTemplateColumns: "1fr",
		},
	},

	docCard: {
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: 8,
		padding: theme.spacing(2),
		backgroundColor: isDark ? "rgba(255, 255, 255, 0.02)" : theme.palette.background.paper,
		minWidth: 0,
	},

	docCardWide: {
		gridColumn: "1 / -1",
	},

	docCardHeader: {
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		gap: theme.spacing(1),
		marginBottom: theme.spacing(1),
		flexWrap: "wrap",
	},

	docCardTitle: {
		fontWeight: 600,
		fontSize: "1rem",
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
	},

	docMetaRow: {
		display: "flex",
		gap: theme.spacing(1),
		alignItems: "center",
		flexWrap: "wrap",
		marginBottom: theme.spacing(1.5),
	},

	docList: {
		margin: 0,
		paddingLeft: theme.spacing(2.5),
	},

	docListItem: {
		marginBottom: theme.spacing(0.75),
		fontSize: "0.92rem",
		lineHeight: 1.6,
	},

	inlineCode: {
		fontFamily: '"Courier New", monospace',
		fontSize: "0.86rem",
		padding: "2px 6px",
		borderRadius: 4,
		backgroundColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.05)",
	},

	legacyHint: {
		color: theme.palette.text.secondary,
		fontSize: "0.85rem",
		lineHeight: 1.5,
		marginTop: theme.spacing(0.5),
	},
	});
});

const Settings = () => {
	const classes = useStyles();
	const [settings, setSettings] = useState([]);
	const [expandedSection, setExpandedSection] = useState("general");
	const [agentsConfig, setAgentsConfig] = useState({
		apiUrl: "",
		apiKey: "",
		companyWebsite: "",
	});
	const tApi = key => i18n.t(`settings.apiDocs.${key}`);
	const tAgents = key => i18n.t(`settings.aiAgents.${key}`);

	useEffect(() => {
		const fetchSession = async () => {
			try {
				const { data } = await api.get("/settings");
				setSettings(data);
			} catch (err) {
				toastError(err);
			}
		};
		fetchSession();
	}, []);

	useEffect(() => {
		const socket = openSocket();

		socket.on("settings", data => {
			if (data.action === "update") {
				setSettings(prevState => {
					const aux = [...prevState];
					const settingIndex = aux.findIndex(s => s.key === data.setting.key);
					if (settingIndex >= 0) {
						aux[settingIndex].value = data.setting.value;
					} else {
						aux.push(data.setting);
					}
					return aux;
				});
			}
		});

		return () => {
			socket.disconnect();
		};
	}, []);

	useEffect(() => {
		setAgentsConfig({
			apiUrl: getSettingValue("flowchAgentsApiUrl") || "https://agents.flowch.com",
			apiKey: getSettingValue("flowchAgentsApiKey") || "",
			companyWebsite: getSettingValue("companyWebsite") || "",
		});
	}, [settings]);

	const handleChangeSetting = async e => {
		const selectedValue = e.target.value;
		const settingKey = e.target.name;

		try {
			await api.put(`/settings/${settingKey}`, {
				value: selectedValue,
			});
			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleAgentsConfigChange = event => {
		const { name, value } = event.target;
		setAgentsConfig(prevState => ({
			...prevState,
			[name]: value,
		}));
	};

	const handleSaveAgentsConfig = async () => {
		try {
			await api.put("/settings/flowchAgentsApiUrl", {
				value: agentsConfig.apiUrl || "https://agents.flowch.com",
			});

			if (agentsConfig.apiKey && agentsConfig.apiKey !== "********") {
				await api.put("/settings/flowchAgentsApiKey", {
					value: agentsConfig.apiKey,
				});
			}

			await api.put("/settings/companyWebsite", {
				value: agentsConfig.companyWebsite.trim(),
			});

			toast.success(i18n.t("settings.success"));
		} catch (err) {
			toastError(err);
		}
	};

	const handleAccordionChange = (panel) => (event, isExpanded) => {
		setExpandedSection(isExpanded ? panel : false);
	};

	const getSettingValue = key => {
		const setting = settings.find(s => s.key === key);
		return setting ? setting.value : "";
	};

	const copyToClipboard = (text) => {
		navigator.clipboard.writeText(text).then(() => {
			toast.success(tApi("copied"));
		});
	};

	const apiToken = settings && settings.length > 0 ? getSettingValue("userApiToken") : "";
	const apiBaseUrl = "https://talk.flowch.com/backend/api/messages";
	const apiSendEndpoint = `${apiBaseUrl}/send`;
	const apiTransferEndpoint = `${apiBaseUrl}/transfer`;
	const apiTemplateEndpoint = `${apiBaseUrl}/whatsapp-official/templates/send`;
	const apiResolveEndpoint = `${apiBaseUrl}/resolve`;

	const sendExamplePayload = `{
  "connectionId": 7,
  "number": "5519978175005",
  "body": "Ola! Esta e uma mensagem de teste.",
  "queueId": 3,
  "userId": 42
}`;

	const sendSuccessPayload = `{
  "ok": true,
  "ticketId": 1243,
  "connectionId": 7,
  "whatsappId": 5,
  "queueId": 3,
  "userId": 42
}`;

	const transferExamplePayload = `{
  "connectionId": 7,
  "number": "5519978175005",
  "queueId": 3,
  "userId": 42,
  "body": "Olá! Vou te direcionar para o setor correto."
}`;

	const transferSuccessPayload = `{
  "id": 1243,
  "status": "pending",
  "queueId": 3,
  "userId": 42
}`;

const templateExamplePayload = `{
  "connectionId": 2,
  "number": "5519978175005",
  "queueId": 4,
  "userId": 42,
  "template": {
    "name": "confirmacao_consulta",
    "languageCode": "pt_BR",
    "parameters": {
      "nome": "Rodrigo",
      "data": "20/09/2026"
    }
  }
}`;

	const templateSuccessPayload = `{
  "ok": true,
  "ticketId": 1243,
  "connectionId": 2,
  "whatsappId": 5,
  "queueId": 4,
  "userId": 42,
  "template": {
    "name": "confirmacao_consulta",
    "languageCode": "pt_BR",
    "parameters": {
      "nome": "Rodrigo",
      "data": "20/09/2026"
    }
  }
}`;

	const resolveExamplePayload = `{
  "number": "5519978175005"
}`;

	const resolveSuccessPayload = `{
  "ok": true,
  "ticketId": 1243,
  "number": "5519978175005",
  "status": "closed",
  "connectionId": 7,
  "whatsappId": 5,
  "queueId": 3
}`;

	return (
		<MainContainer>
			<div className={classes.mainPaper}>
				<Container className={classes.container}>
				<Box className={classes.pageHeader}>
					<Box>
						<Typography variant="h4" className={classes.pageTitle}>
							{i18n.t("settings.title")}
						</Typography>
						<Typography className={classes.pageIntro}>
							{tApi("intro")}
						</Typography>
					</Box>
				</Box>

				{/* GENERAL SETTINGS SECTION */}
				<Accordion 
					expanded={expandedSection === "general"}
					onChange={handleAccordionChange("general")}
					className={classes.accordionSection}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="general-content"
						id="general-header"
						className={classes.accordionSummary}
					>
						<span className={classes.sectionIcon}>
							<SettingsIcon fontSize="small" />
						</span>
						<Box className={classes.sectionHeading}>
							<Typography className={classes.sectionTitle}>
								{tApi("generalTitle")}
							</Typography>
							<Typography className={classes.sectionDescription}>
								{tApi("generalDescription")}
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails className={classes.accordionDetails}>
						<Box className={classes.settingRow}>
							<Typography className={classes.settingLabel}>
								{i18n.t("settings.settings.userCreation.name")}
							</Typography>
							<Select
								margin="dense"
								variant="outlined"
								native
								className={classes.settingControl}
								id="userCreation-setting"
								name="userCreation"
								value={
									settings && settings.length > 0 && getSettingValue("userCreation")
								}
								onChange={handleChangeSetting}
							>
								<option value="enabled">
									{i18n.t("settings.settings.userCreation.options.enabled")}
								</option>
								<option value="disabled">
									{i18n.t("settings.settings.userCreation.options.disabled")}
								</option>
							</Select>
						</Box>
						<Box className={classes.settingRow}>
							<Typography className={classes.settingLabel}>
								{i18n.t("settings.settings.allowSignMessageChoice.name")}
							</Typography>
							<Select
								margin="dense"
								variant="outlined"
								native
								className={classes.settingControl}
								id="allowSignMessageChoice-setting"
								name="allowSignMessageChoice"
								value={
									settings && settings.length > 0 && getSettingValue("allowSignMessageChoice")
								}
								onChange={handleChangeSetting}
							>
								<option value="enabled">
									{i18n.t("settings.settings.allowSignMessageChoice.options.enabled")}
								</option>
								<option value="disabled">
									{i18n.t("settings.settings.allowSignMessageChoice.options.disabled")}
								</option>
							</Select>
						</Box>
					</AccordionDetails>
				</Accordion>

				{/* FLOWCH AGENTS SETTINGS SECTION */}
				<Accordion
					expanded={expandedSection === "agents"}
					onChange={handleAccordionChange("agents")}
					className={classes.accordionSection}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="agents-content"
						id="agents-header"
						className={classes.accordionSummary}
					>
						<span className={classes.sectionIcon}>
							<VpnKeyIcon fontSize="small" />
						</span>
						<Box className={classes.sectionHeading}>
							<Typography className={classes.sectionTitle}>
								{tAgents("title")}
							</Typography>
							<Typography className={classes.sectionDescription}>
								{tAgents("description")}
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails className={classes.accordionDetails}>
						<Typography className={classes.instructionText}>
							{tAgents("help")}
						</Typography>
						<Box className={classes.settingRow}>
							<Box>
								<Typography className={classes.settingLabel}>
									{tAgents("apiUrl")}
								</Typography>
								<Typography className={classes.legacyHint}>
									{tAgents("apiUrlHelp")}
								</Typography>
							</Box>
							<TextField
								name="apiUrl"
								value={agentsConfig.apiUrl}
								onChange={handleAgentsConfigChange}
								variant="outlined"
								size="small"
								className={classes.settingControl}
								fullWidth
							/>
						</Box>
						<Box className={classes.settingRow}>
							<Box>
								<Typography className={classes.settingLabel}>
									{tAgents("apiKey")}
								</Typography>
								<Typography className={classes.legacyHint}>
									{tAgents("apiKeyHelp")}
								</Typography>
							</Box>
							<TextField
								name="apiKey"
								value={agentsConfig.apiKey}
								onChange={handleAgentsConfigChange}
								variant="outlined"
								size="small"
								type="password"
								className={classes.settingControl}
								fullWidth
							/>
						</Box>
						<Box className={classes.settingRow}>
							<Box>
								<Typography className={classes.settingLabel}>
									{tAgents("companyWebsite")}
								</Typography>
								<Typography className={classes.legacyHint}>
									{tAgents("companyWebsiteHelp")}
								</Typography>
							</Box>
							<TextField
								name="companyWebsite"
								value={agentsConfig.companyWebsite}
								onChange={handleAgentsConfigChange}
								variant="outlined"
								size="small"
								placeholder={tAgents("companyWebsitePlaceholder")}
								className={classes.settingControl}
								fullWidth
							/>
						</Box>
						<Box>
							<Button
								variant="contained"
								color="primary"
								disableElevation
								className={classes.copyButton}
								onClick={handleSaveAgentsConfig}
							>
								{tAgents("save")}
							</Button>
						</Box>
						<Box className={classes.instructionBox}>
							<Typography className={classes.instructionTitle}>
								<CodeIcon fontSize="small" />
								{tAgents("internalSkillsTitle")}
							</Typography>
							<Typography className={classes.instructionText}>
								{tAgents("internalSkillsDescription")}
							</Typography>
							<Box className={classes.codeBlock}>
{`POST ${getSettingValue("flowchAgentsApiUrl") || "https://agents.flowch.com"} -> ${window.location.origin}/backend/ai/internal-skills/{skillName}
Headers:
Content-Type: application/json
X-Flowch-Signature: sha256=<hmac_sha256_body>

Body:
{
  "companyId": 1,
  "ticketId": 123,
  "queueId": 3,
  "reason": "Cliente pediu atendimento humano"
}`}
							</Box>
							<ul className={classes.docList}>
								<li className={classes.docListItem}>{tAgents("skillQueryContext")}</li>
								<li className={classes.docListItem}>{tAgents("skillRequestHuman")}</li>
								<li className={classes.docListItem}>{tAgents("skillTransferQueue")}</li>
								<li className={classes.docListItem}>{tAgents("skillCloseAttendance")}</li>
								<li className={classes.docListItem}>{tAgents("skillAddNote")}</li>
								<li className={classes.docListItem}>{tAgents("skillUpdateContact")}</li>
							</ul>
							<Typography className={classes.legacyHint}>
								{tAgents("internalSkillsValidation")}
							</Typography>
						</Box>
					</AccordionDetails>
				</Accordion>

				{/* API SETTINGS SECTION */}
				<OrganizationSettings />

				<Accordion 
					expanded={expandedSection === "api"}
					onChange={handleAccordionChange("api")}
					className={classes.accordionSection}
				>
					<AccordionSummary
						expandIcon={<ExpandMoreIcon />}
						aria-controls="api-content"
						id="api-header"
						className={classes.accordionSummary}
					>
						<span className={classes.sectionIcon}>
							<CodeIcon fontSize="small" />
						</span>
						<Box className={classes.sectionHeading}>
							<Typography className={classes.sectionTitle}>
								{tApi("apiTitle")}
							</Typography>
							<Typography className={classes.sectionDescription}>
								{tApi("apiDescription")}
							</Typography>
						</Box>
					</AccordionSummary>
					<AccordionDetails className={classes.accordionDetails}>
						{/* TOKEN FIELD */}
						<Box className={classes.tokenPanel}>
							<Box>
								<Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 4 }}>
									{tApi("tokenTitle")}
								</Typography>
								<Typography className={classes.legacyHint}>
									{tApi("tokenHelp")}
								</Typography>
								<TextField
									id="api-token-setting"
									variant="outlined"
									fullWidth
									size="small"
									value={apiToken}
									InputProps={{
										readOnly: true,
									}}
									className={classes.tokenField}
								/>
							</Box>
							<Button
								variant="outlined"
								size="small"
								startIcon={<FileCopyIcon />}
								onClick={() => copyToClipboard(apiToken)}
								className={classes.copyButton}
							>
								{tApi("copyToken")}
							</Button>
						</Box>

						<AiHistoryApiDocs />
						{/* INSTRUCTIONS */}
						<Box className={classes.instructionBox}>
							<Typography className={classes.instructionTitle}>
								<CodeIcon fontSize="small" />
								{tApi("quickDocTitle")}
							</Typography>

							<Box className={classes.docsSection}>
								<Box className={classes.docCard}>
									<Typography className={classes.docCardTitle}>
										<VpnKeyIcon fontSize="small" />
										{tApi("overview")}
									</Typography>
									<ul className={classes.docList}>
										<li className={classes.docListItem}>
											{tApi("overviewToken")}
										</li>
										<li className={classes.docListItem}>
											{tApi("overviewNumber")}
										</li>
										<li className={classes.docListItem}>
											{tApi("overviewConnection")}
										</li>
										<li className={classes.docListItem}>
											{tApi("overviewTransfer")}
										</li>
										<li className={classes.docListItem}>
											{tApi("overviewResolve")}
										</li>
									</ul>
								</Box>

								<Box className={classes.docCard}>
									<Typography className={classes.docCardTitle}>
										{tApi("requiredHeaders")}
									</Typography>
									<Box className={classes.codeBlock}>
{`Authorization: Bearer ${apiToken || "SEU_TOKEN"}
Content-Type: application/json`}
									</Box>
								</Box>

								<Box className={`${classes.docCard} ${classes.docCardWide}`}>
									<Box className={classes.docCardHeader}>
										<Typography className={classes.docCardTitle}>
											<SendIcon fontSize="small" />
											{tApi("sendMessage")}
										</Typography>
										<Chip label="POST" color="primary" size="small" />
									</Box>

									<Box className={classes.docMetaRow}>
										<span className={classes.inlineCode}>{apiSendEndpoint}</span>
									</Box>

									<Typography className={classes.instructionText}>
										{tApi("sendDescription")}
									</Typography>

									<ul className={classes.docList}>
										<li className={classes.docListItem}><strong>connectionId</strong>: {tApi("recommendedNew")}</li>
										<li className={classes.docListItem}><strong>number</strong>: {tApi("required")}</li>
										<li className={classes.docListItem}><strong>body</strong>: {tApi("requiredWithoutMedia")}</li>
										<li className={classes.docListItem}><strong>queueId</strong>: {tApi("optionalQueue")}</li>
										<li className={classes.docListItem}><strong>userId</strong>: {tApi("optionalUser")}</li>
										<li className={classes.docListItem}><strong>whatsappId</strong>: {tApi("optionalLegacy")}</li>
										<li className={classes.docListItem}><strong>quotedMsg</strong>: {tApi("optional")}</li>
									</ul>

									<Box className={classes.codeBlock}>{sendExamplePayload}</Box>
									<Typography className={classes.legacyHint}>
										{tApi("userAssignmentValidation")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("aiContextHandoff")}
									</Typography>
									<Typography className={classes.instructionText}>
										{tApi("successResponse")}
									</Typography>
									<Box className={classes.codeBlock}>{sendSuccessPayload}</Box>

									<Box className={classes.codeBlock}>
{`curl -X POST ${apiSendEndpoint} \\
  -H "Authorization: Bearer ${apiToken || "SEU_TOKEN"}" \\
  -H "Content-Type: application/json" \\
  -d '${sendExamplePayload}'`}
									</Box>
								</Box>

								<Box className={`${classes.docCard} ${classes.docCardWide}`}>
									<Box className={classes.docCardHeader}>
										<Typography className={classes.docCardTitle}>
											<SwapHorizIcon fontSize="small" />
											{tApi("transferQueue")}
										</Typography>
										<Chip label="POST" color="secondary" size="small" />
									</Box>

									<Box className={classes.docMetaRow}>
										<span className={classes.inlineCode}>{apiTransferEndpoint}</span>
									</Box>

									<Typography className={classes.instructionText}>
										{tApi("transferDescription")}
									</Typography>

									<ul className={classes.docList}>
										<li className={classes.docListItem}><strong>connectionId</strong>: {tApi("recommendedNew")}</li>
										<li className={classes.docListItem}><strong>number</strong>: {tApi("required")}</li>
										<li className={classes.docListItem}><strong>queueId</strong>: {tApi("required")}</li>
										<li className={classes.docListItem}><strong>userId</strong>: {tApi("optionalUser")}</li>
										<li className={classes.docListItem}><strong>body</strong>: {tApi("optional")}</li>
										<li className={classes.docListItem}><strong>whatsappId</strong>: {tApi("optionalLegacy")}</li>
									</ul>

									<Box className={classes.codeBlock}>{transferExamplePayload}</Box>
									<Typography className={classes.legacyHint}>
										{tApi("userAssignmentValidation")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("aiContextHandoff")}
									</Typography>
									<Typography className={classes.instructionText}>
										{tApi("successResponse")}
									</Typography>
									<Box className={classes.codeBlock}>{transferSuccessPayload}</Box>

									<Box className={classes.codeBlock}>
{`curl -X POST ${apiTransferEndpoint} \\
  -H "Authorization: Bearer ${apiToken || "SEU_TOKEN"}" \\
  -H "Content-Type: application/json" \\
  -d '${transferExamplePayload}'`}
									</Box>
								</Box>

								<Box className={`${classes.docCard} ${classes.docCardWide}`}>
									<Box className={classes.docCardHeader}>
										<Typography className={classes.docCardTitle}>
											<DescriptionIcon fontSize="small" />
											{tApi("sendTemplate")}
										</Typography>
										<Chip label="POST" color="primary" size="small" />
									</Box>

									<Box className={classes.docMetaRow}>
										<span className={classes.inlineCode}>{apiTemplateEndpoint}</span>
									</Box>

									<Typography className={classes.instructionText}>
										{tApi("templateDescription")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("templateConnectionIdHint")}
									</Typography>

									<ul className={classes.docList}>
										<li className={classes.docListItem}><strong>connectionId</strong>: {tApi("templateConnectionRequired")}</li>
										<li className={classes.docListItem}><strong>number</strong>: {tApi("required")}</li>
										<li className={classes.docListItem}><strong>queueId</strong>: {tApi("optionalQueue")}</li>
										<li className={classes.docListItem}><strong>userId</strong>: {tApi("optionalUser")}</li>
										<li className={classes.docListItem}><strong>template.name</strong>: {tApi("templateNameRequired")}</li>
										<li className={classes.docListItem}><strong>template.languageCode</strong>: {tApi("templateLanguage")}</li>
										<li className={classes.docListItem}><strong>template.parameters</strong>: {tApi("templateParameters")}</li>
									</ul>

									<Box className={classes.codeBlock}>{templateExamplePayload}</Box>
									<Typography className={classes.legacyHint}>
										{tApi("userAssignmentValidation")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("aiContextHandoff")}
									</Typography>
									<Typography className={classes.instructionText}>
										{tApi("successResponse")}
									</Typography>
									<Box className={classes.codeBlock}>{templateSuccessPayload}</Box>

									<Box className={classes.codeBlock}>
{`curl -X POST ${apiTemplateEndpoint} \\
  -H "Authorization: Bearer ${apiToken || "SEU_TOKEN"}" \\
  -H "Content-Type: application/json" \\
  -d '${templateExamplePayload}'`}
									</Box>
								</Box>

								<Box className={`${classes.docCard} ${classes.docCardWide}`}>
									<Box className={classes.docCardHeader}>
										<Typography className={classes.docCardTitle}>
											<CheckCircleOutlineIcon fontSize="small" />
											{tApi("resolveConversation")}
										</Typography>
										<Chip label="POST" color="secondary" size="small" />
									</Box>

									<Box className={classes.docMetaRow}>
										<span className={classes.inlineCode}>{apiResolveEndpoint}</span>
									</Box>

									<Typography className={classes.instructionText}>
										{tApi("resolveDescription")}
									</Typography>

									<ul className={classes.docList}>
										<li className={classes.docListItem}><strong>number</strong>: {tApi("resolveNumberRequired")}</li>
									</ul>

									<Typography className={classes.legacyHint}>
										{tApi("resolveBehavior")}
									</Typography>

									<Box className={classes.codeBlock}>{resolveExamplePayload}</Box>

									<Box className={classes.codeBlock}>
{`curl -X POST ${apiResolveEndpoint} \\
  -H "Authorization: Bearer ${apiToken || "SEU_TOKEN"}" \\
  -H "Content-Type: application/json" \\
  -d '${resolveExamplePayload}'`}
									</Box>

									<Typography className={classes.instructionText}>
										{tApi("resolveSuccess")}
									</Typography>
									<Box className={classes.codeBlock}>{resolveSuccessPayload}</Box>
									<Typography className={classes.legacyHint}>
										{tApi("resolveValidation")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("resolveAlreadyClosed")}
									</Typography>
									<Typography className={classes.legacyHint}>
										{tApi("resolveMethod")}
									</Typography>
								</Box>

								<Box className={classes.docCard}>
									<Typography className={classes.docCardTitle}>
										{tApi("javascriptExample")}
									</Typography>
									<Box className={classes.codeBlock}>
{`const token = "${apiToken || "SEU_TOKEN"}";

const payload = {
  connectionId: 7,
  number: "5519978175005",
  body: "Ola! Esta e uma mensagem de teste.",
  queueId: 3,
  userId: 42
};

fetch("${apiSendEndpoint}", {
  method: "POST",
  headers: {
    Authorization: \`Bearer \${token}\`,
    "Content-Type": "application/json"
  },
  body: JSON.stringify(payload)
})
  .then(res => res.json())
  .then(data => console.log(data));`}
									</Box>
								</Box>

								<Box className={classes.docCard}>
									<Typography className={classes.docCardTitle}>
										{tApi("expectedResponses")}
									</Typography>
									<ul className={classes.docList}>
										<li className={classes.docListItem}><strong>200 OK</strong>: {tApi("response200")}</li>
										<li className={classes.docListItem}><strong>400 Bad Request</strong>: {tApi("response400")}</li>
										<li className={classes.docListItem}><strong>409 Conflict</strong>: {tApi("response409")}</li>
										<li className={classes.docListItem}><strong>401 / 403</strong>: {tApi("response401")}</li>
										<li className={classes.docListItem}><strong>404</strong>: {tApi("response404")}</li>
										<li className={classes.docListItem}><strong>503</strong>: {tApi("response503")}</li>
									</ul>

									<Box className={classes.codeBlock}>
{`{
  "error": "ERR_SESSION_EXPIRED"
}`}
									</Box>
								</Box>
							</Box>
						</Box>
					</AccordionDetails>
				</Accordion>
				</Container>
			</div>
		</MainContainer>
	);
};

export default Settings;
